import { IArticleCardData, IArticleCard, IArticleCardAction } from 'models/magazine/article/articlecard';
import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { IUserPreferences } from 'models/user/userpreferences';
import { ISuggestedArticleGallery } from 'models/magazine/article/suggestedArticleGallery';
const { notifSend } = notifActions;

/** Article Comments Module */
import { ArticleStatus } from 'models/enums/statuses';
import { getArticle, clearManageArticleFormError } from 'redux/modules/magazine/article/managearticle';
import { Likes } from 'redux/modules/user/likes';
import { LikeNodeType } from 'models/enums/nodetype/likenodetype';
import { browserHistory } from 'react-router';
import { goToErrorPage } from '../../../error';
import { Wishlists } from '../../../user/wishlists';
import { WishlistNodeType } from '../../../../../models/enums/nodetype/wishlistnodetype';
import { IArticleProductItem } from '../../../../../models/magazine/article/articleproductitem';

/** Action Types */
export const REQUEST_START: string = 'articlecard/REQUEST_START';
export const REQUEST_FAILURE: string = 'articlecard/REQUEST_FAILURE';
export const GET_ARTICLE_CARD_SUCCESS: string = 'articlecard/GET_ARTICLE_CARD_SUCCESS';
export const GET_SUGGESTED_ARTICLE_GALLERIES_SUCCESS: string = 'articlecard/GET_SUGGESTED_ARTICLE_GALLERIES_SUCCESS';
export const SET_OPEN_GALLERY: string = 'articlecard/SET_OPEN_GALLERY';
export const SET_SELECTED_ARTICLE_GALLERY: string = 'articlecard/SET_SELECTED_ARTICLE_GALLERY';
export const SET_IS_MY_ARTICLE: string = 'articlecard/SET_IS_MY_ARTICLE';
export const LIKE_ITEM_SUCCESS: string = 'articlecard/LIKE_ITEM_SUCCESS';
export const WISHLIST_ITEM_SUCCESS: string = 'articlecard/WISHLIST_ITEM_SUCCESS';
export const SET_ARTICLE_FEATURED_PRODUCTS: string = 'articlecard/SET_ARTICLE_FEATURED_PRODUCTS';

/** Initial State */
const initialState: IArticleCardData = {
	item: null,
	isFetching: false,
	suggestedArticleGalleries: null,
	openGallery: false,
	selectedArticleGallery: null,
	isMyArticle: false,
};

/** Reducer */
export function articleCardReducer(state = initialState, action: IArticleCardAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_ARTICLE_CARD_SUCCESS:
			return Object.assign({}, state, {
				item: action.payload.item,
				isFetching: false,
			});

		case GET_SUGGESTED_ARTICLE_GALLERIES_SUCCESS:
			return Object.assign({}, state, {
				suggestedArticleGalleries: action.payload.suggestedArticleGalleries,
				isFetching: false,
			});

		case SET_OPEN_GALLERY:
			return Object.assign({}, state, {
				openGallery: action.payload.openGallery,
			});

		case SET_SELECTED_ARTICLE_GALLERY:
			return Object.assign({}, state, {
				selectedArticleGallery: action.payload.selectedArticleGallery,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_IS_MY_ARTICLE:
			return Object.assign({}, state, {
				isMyArticle: action.payload.isMyArticle,
			});

		case LIKE_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
			});

		case WISHLIST_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
			});

		case SET_ARTICLE_FEATURED_PRODUCTS:
			return Object.assign({}, state, {
				item: {
					...state.item,
					itemsJArray: action.payload.itemsJArray,
				},
				isFetching: false,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getArticleCardByCode(code: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const userPreferences: IUserPreferences = getState().userPreferences;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/' + code + '/GetByCode',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IArticleCard) => {
						setArticleCardData(res, userPreferences, dispatch);
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => dispatch(requestFailure(err)));
	};
}

/** Action Creator */
export function getArticleCard(id: string, target?: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const userPreferences: IUserPreferences = getState().userPreferences;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/' + id,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IArticleCard) => {
						setArticleCardData(res, userPreferences, dispatch);
						const url = `/magazine/article/${res.code}`;
						if (target && target.length > 0) {
							window.open(url, target);
						} else {
							browserHistory.push(url);
						}
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(goToErrorPage(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(goToErrorPage(message));
						});
				}
			})
			.catch((err) => dispatch(goToErrorPage(err)));
	};
}

function setArticleCardData(res: IArticleCard, userPreferences: IUserPreferences, dispatch) {
	dispatch(getArticleCardSuccess(res));
	const likes = new Likes(LikeNodeType.ArticleCard);
	const wishlists = new Wishlists(WishlistNodeType.ArticleFeaturedProducts);
	if (userPreferences.id !== null) {
		dispatch(setIsMyArticle(userPreferences.id === res.userId));
		dispatch(likes.getLikedItems());

		if (res.itemsJArray && res.itemsJArray.length > 0) {
			dispatch(wishlists.getWishlistedItems());
		}
	} else {
		dispatch(likes.updateLikeItems(null));
		dispatch(wishlists.updateWishlistItems(null));
	}
}
/** Action Creator */
export function getSuggestedArticleGalleries(code: string, isSuggestedArticleGallery: boolean) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const articleCardStore: IArticleCardData = getState().articleCard;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/' + code + '/SuggestedGalleries',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: ISuggestedArticleGallery[]) => {
						if (isSuggestedArticleGallery) {
							const selectedArticleGallery = articleCardStore.suggestedArticleGalleries.find((x) => x.code === code);
							dispatch(setSelectedArticleGallery(selectedArticleGallery));
						}
						dispatch(getSuggestedArticleGalleriesSuccess(res));
						dispatch(setOpenGallery(true));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => dispatch(requestFailure(err)));
	};
}

/** Action Creator */
export function setArticleStatus(id: string, status: ArticleStatus) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Article/' + id + '/SetStatus/' + status,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
			}),
		)
			.then((res) => {
				if (res.ok) {
					if (getState().articleCard.item) {
						dispatch(updateArticleStatus(status));
					}

					if (getState().manageArticle.article) {
						dispatch(getArticle(id));
					}

					if (status === ArticleStatus.Approved) {
						dispatch(clearManageArticleFormError());
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_Article_Approved'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
					}
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function updateArticleStatus(status: ArticleStatus) {
	return (dispatch, getState: () => IStore) => {
		const articleCardState = getState().articleCard;
		const item = articleCardState.item;
		item.status = status;
		dispatch(getArticleCardSuccess(item));
	};
}

/** Action Creator */
export function requestStart(): IArticleCardAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getArticleCardSuccess(item: IArticleCard): IArticleCardAction {
	return {
		type: GET_ARTICLE_CARD_SUCCESS,
		payload: {
			item,
		},
	};
}

/** Action Creator */
export function getSuggestedArticleGalleriesSuccess(galleries: ISuggestedArticleGallery[]): IArticleCardAction {
	return {
		type: GET_SUGGESTED_ARTICLE_GALLERIES_SUCCESS,
		payload: {
			suggestedArticleGalleries: galleries,
		},
	};
}

/** Action Creator */
export function setOpenGallery(openGallery: boolean): IArticleCardAction {
	return {
		type: SET_OPEN_GALLERY,
		payload: {
			openGallery,
		},
	};
}

/** Action Creator */
export function setSelectedArticleGallery(selectedArticleGallery: ISuggestedArticleGallery): IArticleCardAction {
	return {
		type: SET_SELECTED_ARTICLE_GALLERY,
		payload: {
			selectedArticleGallery,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IArticleCardAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setIsMyArticle(isMyArticle: boolean): IArticleCardAction {
	return {
		type: SET_IS_MY_ARTICLE,
		payload: {
			isMyArticle,
		},
	};
}

/** Action Creator */
export function likeItemSuccess(): IArticleCardAction {
	return {
		type: LIKE_ITEM_SUCCESS,
	};
}

export function wishlistItemSuccess(): IArticleCardAction {
	return {
		type: WISHLIST_ITEM_SUCCESS,
	};
}

export function setArticleFeaturedProducts(itemsJArray: IArticleProductItem[]): IArticleCardAction {
	return {
		type: SET_ARTICLE_FEATURED_PRODUCTS,
		payload: {
			itemsJArray,
		},
	};
}
